"use client";
import { useEffect, useState } from "react";

export default function NewsletterList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const res = await fetch(
          "https://eventscons.com/eventbackendnew/api/newsletter/all"
        );

        if (!res.ok) throw new Error("Failed to fetch data");

        const json = await res.json();
        setData(json.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletter();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete subscriber #${id}?`
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);

      const res = await fetch(
        `https://eventscons.com/eventbackendnew/api/newsletter/${id}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error("Delete error:", errData);
        alert(errData?.message || "Failed to delete subscriber.");
        return;
      }

      setData((prev) => prev.filter((item) => item.id !== id));
      alert("Subscriber deleted successfully!");
    } catch (err) {
      console.error("Delete request failed:", err);
      alert("Something went wrong while deleting.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>📩 Newsletter Subscribers</h2>
          <div style={styles.stats}>
            <span style={styles.stat}>Total: {data.length}</span>
          </div>
        </div>

        {loading && <div style={styles.loading}>Loading subscribers...</div>}
        {error && <div style={styles.error}>Error: {error}</div>}

        {!loading && !error && (
          <div style={styles.tableWrapper}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>IP Address</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Subscribed On</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr key={item.id} style={styles.tr}>
                      <td style={styles.td}>{item.id}</td>
                      <td style={styles.td}>
                        <div style={styles.emailCell}>{item.email}</div>
                      </td>
                      <td style={styles.td}>{item.ip_address}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            ...styles.badgeActive,
                            ...(item.is_active ? {} : styles.badgeInactive),
                          }}
                        >
                          {item.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.dateCell}>
                          {new Date(item.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                          style={{
                            ...styles.deleteBtn,
                            ...(deletingId === item.id && styles.deleteBtnLoading),
                          }}
                        >
                          {deletingId === item.id ? (
                            <>
                              <span style={styles.spinner}></span>
                              Deleting...
                            </>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📭</div>
                <h3 style={styles.emptyTitle}>No subscribers yet</h3>
                <p style={styles.emptyText}>Newsletter subscribers will appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const styles = {
  page: {
    minHeight: "100vh",
    background: "#fff",
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "1200px",
    background: "#ffffff",
    borderRadius: "20px",
    padding: "0",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflow: "hidden",
  },
  header: {
    padding: "30px 30px 20px",
    background: '#9b1321',
    color: "#fff",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    margin: "0 0 10px 0",
    letterSpacing: "-0.025em",
    color:'#fff',
  },
  stats: {
    fontSize: "1rem",
    opacity: 0.9,
  },
  stat: {
    background: "rgba(255,255,255,0.15)",
    padding: "6px 14px",
    borderRadius: "50px",
    fontWeight: 500,
  },
  tableWrapper: {
    padding: "0 30px 30px",
  },
  tableContainer: {
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "0.95rem",
  },
  tr: {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  th: {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    padding: "18px 20px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#475569",
    borderBottom: "2px solid #e2e8f0",
    position: "sticky",
    top: 0,
    zIndex: 10,
    letterSpacing: "0.025em",
    textTransform: "uppercase",
  },
  td: {
    padding: "16px 20px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },
  emailCell: {
    fontWeight: 500,
    color: "#1e293b",
    fontSize: "0.95rem",
  },
  dateCell: {
    fontSize: "0.85rem",
    color: "#64748b",
  },
  badge: {
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: 600,
    display: "inline-block",
    letterSpacing: "0.025em",
  },
  badgeActive: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  badgeInactive: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  deleteBtn: {
    padding: "8px 16px",
    fontSize: "0.8rem",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#fff",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
  },
  deleteBtnLoading: {
    opacity: 0.8,
    cursor: "not-allowed",
    background: "#9ca3af",
    boxShadow: "none",
  },
  spinner: {
    width: "12px",
    height: "12px",
    border: "1.5px solid rgba(255,255,255,0.3)",
    borderTop: "1.5px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loading: {
    padding: "60px 30px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "1.1rem",
  },
  error: {
    padding: "40px 30px",
    textAlign: "center",
    color: "#ef4444",
    fontSize: "1.1rem",
    background: "#fef2f2",
    borderRadius: "12px",
    border: "1px solid #fecaca",
  },
  emptyState: {
    padding: "80px 30px",
    textAlign: "center",
    color: "#64748b",
  },
  emptyIcon: {
    fontSize: "4rem",
    marginBottom: "20px",
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#475569",
    margin: "0 0 10px 0",
  },
  emptyText: {
    fontSize: "1rem",
    margin: 0,
  },
};
